'use client';

import { notFound, useParams } from 'next/navigation';

import { useAppQuery, useAppValidationQuery, useFeatureTogglesQuery, useTokenQuery } from '@query';

import { AppValidation, DeserializedAppData, DeserializedTokenData } from '@api';

import { Faq, SubgroupStringContent } from '@shared/api/types/translation';
import { APP_STATUS, getAppStatus } from '@shared/helpers/app';
import { useTranslationTreeQuery } from '@shared/hooks/query/translations';
import { useLanguage } from '@shared/hooks/useLanguage';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';

import AddValidationProcessSection from './AddValidationProcessSection';
import AddValidationResultsSection from './AddValidationResultsSection';
import AppDetailsSection from './AppDetailsSection';
import AppFaq from './AppFaq';
import AppRecommendationsSections from './AppRecommendationsSections';
import AppSuggestionsSection from './AppSuggestionsSection';
import AppTokenSection from './AppTokenSection';
import AppTrustScoreSection from './AppTrustScoreSection';
import AppUserScoreSection from './AppUserScoreSection';
import { MeetTheTeamSection } from './MeetTheTeamSection';

export interface AppPageSectionProps {
  app: DeserializedAppData;
  validation?: AppValidation;
  token?: DeserializedTokenData;
  appTranslationData?: SubgroupStringContent;
}

const AppPage = ({ idApp }: { idApp?: string }) => {
  const { appId: id } = useParams() as { appId: string };
  const appId = idApp || id;
  const { data: app, isPending: isLoading } = useAppQuery(appId);
  const { data: featureToggle } = useFeatureTogglesQuery();
  const { data: token } = useTokenQuery(
    {
      tokenId: app?.attributes.tokenId || '',
    },
    {
      enabled: !!app?.attributes.tokenId,
      retry: false,
    },
  );
  const { data: validation } = useAppValidationQuery(appId, {
    enabled: !!app && featureToggle?.appValidation,
  });
  const { data: allTranslationsData, isPending: isAllTranslationsDataLoading } =
    useTranslationTreeQuery(
      {
        searchParams: { lang: 'en', product: 'store', group: 'application' },
      },
      [app?.id.toString() || ''],
    );

  const { currentLanguage } = useLanguage();

  if (!app && !isLoading) {
    notFound();
  }

  if (isLoading || isAllTranslationsDataLoading) {
    return null;
  }

  const {
    attributes: { productionReady },
  } = app;
  const appStatus = getAppStatus(app.attributes);

  const appTranslationData = allTranslationsData?.[currentLanguage]?.store?.application[
    app.id
  ] as SubgroupStringContent;
  const appTranslationFaqData =
    (allTranslationsData?.[currentLanguage]?.store?.application[app.id]?.faq as Faq[]) || [];
  const isReadOnly = appStatus === APP_STATUS.READ_ONLY;
  const showValidation = featureToggle?.appValidation && validation && !isReadOnly;
  const showScoreAndTrust = productionReady && !isReadOnly;
  const showSuggestion = !productionReady && !isReadOnly;
  const showToken = !!token;
  const showFaq = !!appTranslationFaqData?.length;
  const showAppRecommend = !!app.recommendedApps?.length;
  const showMeetTheTeam = appStatus === APP_STATUS.UPCOMING && appTranslationData?.meet_the_team;

  return (
    <>
      <PageHeader
        page="apps"
        disableBanner
        extendBreadcrumbs={[
          {
            label: app.attributes.appName,
            pathname: `/app/${app.attributes.appLink}`,
          },
        ]}
      />
      <main className="v2-container flex flex-col gap-7">
        <AppDetailsSection
          app={app}
          validation={validation}
          token={token}
          appTranslationData={appTranslationData}
        />

        {showMeetTheTeam && <MeetTheTeamSection appTranslationData={appTranslationData} />}

        {showValidation && (
          <>
            {appStatus === APP_STATUS.UNDER_VALIDATION && (
              <AddValidationProcessSection app={app} validation={validation} />
            )}
            {appStatus === APP_STATUS.VALIDATED && (
              <AddValidationResultsSection app={app} validation={validation} />
            )}
          </>
        )}

        {showScoreAndTrust && <AppUserScoreSection app={app} />}

        {showSuggestion && <AppSuggestionsSection app={app} />}

        {showToken && (
          <AppTokenSection
            token={token}
            tokenOverview={appTranslationData?.token_overview || ''}
            tokenSummary={appTranslationData?.token_summary || ''}
            tokenWhitepaper={appTranslationData?.token_whitepaper || ''}
          />
        )}

        {showScoreAndTrust && (
          <>
            <hr className="border-primary-200 sm:hidden" />
            <AppTrustScoreSection app={app} token={token} />
          </>
        )}

        {showFaq && (
          <>
            <hr className="border-primary-200 sm:hidden" />
            <AppFaq appName={app.attributes.appName} faq={appTranslationFaqData} />
          </>
        )}

        {showAppRecommend && (
          <>
            <hr className="border-primary-200 sm:hidden" />
            <AppRecommendationsSections app={app} />
          </>
        )}
      </main>
    </>
  );
};

export default AppPage;
